import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import DataTable from "react-data-table-component";
import {
    Box, Typography, IconButton, Chip, TextField, useTheme,
    Select, MenuItem, FormControl, InputLabel, Tooltip, Alert
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

const ExpandedComponent = ({ data }) => (
    <Box sx={{ p: 2, pl: 5, bgcolor: 'action.hover', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Full Value:</strong> {data.attribute_value || '—'}
        </Typography>
        {data.notes && (
            <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Notes:</strong> {data.notes}
            </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
            Source: {data.source_type || 'manual'}
            {data.source_id ? ` #${data.source_id}` : ''}
            {' | '}Raw Date: {data.raw_date_text || '—'}
            {' | '}Recurring: {data.is_recurring ? `Yes (${data.recurrence_pattern})` : 'No'}
            {' | '}Created: {data.created_at ? new Date(data.created_at).toLocaleDateString() : '—'}
        </Typography>
    </Box>
);

export default function ListFacts({ refresh, onEdit, canModify, canDelete }) {
    const { chatbotId } = useParams();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [facts, setFacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        fetchFacts();
    }, [chatbotId, refresh]);

    const fetchFacts = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/api/facts', {
                params: { chatbot_id: chatbotId, per_page: 500 }
            });
            if (response.data.success) {
                setFacts(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching facts:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (factId) => {
        if (!window.confirm('Are you sure you want to delete this fact?')) return;
        try {
            await axiosInstance.delete(`/api/facts/${factId}`, {
                params: { chatbot_id: chatbotId }
            });
            fetchFacts();
        } catch (error) {
            console.error('Error deleting fact:', error);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Active': return 'success';
            case 'Expired': return 'error';
            case 'Pending_Review': return 'warning';
            default: return 'default';
        }
    };

    const filteredFacts = useMemo(() => {
        return facts.filter(fact => {
            const matchesSearch = !searchText ||
                [fact.entity_name, fact.attribute_name, fact.attribute_value]
                    .join(' ').toLowerCase().includes(searchText.toLowerCase());
            const matchesStatus = statusFilter === 'all' || fact.status === statusFilter;
            return matchesSearch && matchesStatus;
        });
    }, [facts, searchText, statusFilter]);

    const columns = [
        {
            name: 'Entity',
            selector: row => row.entity_name,
            sortable: true,
            width: '160px',
            cell: row => (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.entity_name}
                </Typography>
            )
        },
        {
            name: 'Attribute',
            selector: row => row.attribute_name,
            sortable: true,
            width: '150px',
        },
        {
            name: 'Value',
            selector: row => row.attribute_value,
            sortable: false,
            grow: 2,
            cell: row => (
                <Typography variant="body2" noWrap sx={{ maxWidth: 250 }} title={row.attribute_value}>
                    {row.attribute_value}
                </Typography>
            )
        },
        {
            name: 'Status',
            selector: row => row.status,
            sortable: true,
            width: '130px',
            cell: row => (
                <Chip
                    label={row.status?.replace('_', ' ')}
                    size="small"
                    color={getStatusColor(row.status)}
                    variant="filled"
                />
            )
        },
        {
            name: 'Good Until',
            selector: row => row.good_until_date,
            sortable: true,
            width: '120px',
            cell: row => row.good_until_date
                ? new Date(row.good_until_date).toLocaleDateString()
                : '∞'
        },
        {
            name: 'Confidence',
            selector: row => row.confidence_score,
            sortable: true,
            width: '100px',
            cell: row => `${(row.confidence_score * 100).toFixed(0)}%`
        },
        {
            name: 'Actions',
            width: '100px',
            cell: row => (
                <Box sx={{ display: 'flex' }}>
                    {canModify && (
                        <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => onEdit(row)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {canDelete && (
                        <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            )
        }
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    placeholder="Search entities, attributes, values..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    sx={{ minWidth: 300 }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="Active">Active</MenuItem>
                        <MenuItem value="Expired">Expired</MenuItem>
                        <MenuItem value="Pending_Review">Pending Review</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <DataTable
                columns={columns}
                data={filteredFacts}
                progressPending={loading}
                pagination
                paginationPerPage={20}
                paginationRowsPerPageOptions={[10, 20, 50, 100]}
                expandableRows
                expandableRowsComponent={ExpandedComponent}
                highlightOnHover
                dense
                theme={isDark ? 'dark' : 'default'}
                customStyles={{
                    headCells: { style: { fontWeight: 600, fontSize: '0.8rem' } },
                    cells: { style: { fontSize: '0.85rem' } },
                }}
                noDataComponent={
                    <Alert severity="info" sx={{ m: 2, width: '100%' }}>
                        No facts found. Add facts manually or use "Extract Facts" to automatically extract from your data sources.
                    </Alert>
                }
            />
        </Box>
    );
}
